import { SortingDirection } from 'spw-components/collab/types';
import { EntityList } from 'spw-redux/types';
import { ClientSlimForList } from '../clients/types';
import { Address, IProject } from '../projects/types';
import { IUser } from '../user/types';

export interface BenchmarkReportsState {
  isLoadingProjects?: boolean;
  projects: IProject[];
  selectedProjects: BenchmarkProject[];
  projectsLocations?: Address[];
  projectsUSFRange?: { min: number; max: number };
  summaryIsLoading: boolean;
  summary?: BenchmarkReportSummary;
  clients?: ClientSlimForList[];
  currentReport?: BenchmarkReport;
  currentReportCategoriesMap?: BenchmarkReportCategoriesMap;
  currentReportIconsMap?: BenchmarkReportIconsMap;
  currentReportIsLoading?: boolean;
  reportsList?: EntityList<BenchmarkReport>;
  totalReportsCount?: number;
  editors?: BenchmarkReportEditor[];
  highlightedReportId?: number;
  missingProjectData?: MissingProjectData;
  missingData?: MissingData;
}

export type BenchmarkReportIconsMap = Record<
  BenchmarkReportCardType,
  { [key in BenchmarkReportIconType]: string }
>;

export type BenchmarkReportCategoriesMap = Record<
  string,
  Partial<BenchmarkReportCategory>
>;

export interface BenchmarkProject {
  id?: number;
  linked: boolean;
  forciblyUnlinked?: boolean;
  projectId: number;
  project?: IProject;
}

export interface BenchmarkReportEditor {
  id?: number;
  user: IUser;
  benchmarkReportId?: number;
}

export enum UserRoleInReport {
  viewer = 'VIEWER',
  editor = 'EDITOR',
  owner = 'OWNER',
  orgAdmin = 'ORG_ADMIN',
}

interface MissingProjectDataField {
  field: string;
  isResolved: boolean;
}

export interface MissingFloorDataField {
  id?: number;
  field: string;
  fieldId: number;
  quantity?: number;
  isResolved: boolean;
}

interface MissingFloorData {
  floorId: number;
  missingFields: MissingFloorDataField[];
}

export interface MissingBuildingData {
  buildingId: number;
  missingFloorData: MissingFloorData[];
}

export interface MissingProjectData {
  iwSpacesMissingUsf: number;
  iwSpacesMissingSeatcount: number;
  meetingSpacesMissingUsf: number;
  meetingSpacesMissingSeatcount: number;
  amenitySpacesMissingUsf: number;
  amenitySpacesMissingSeatcount: number;
  supportSpacesMissingUsf: number;
  floorsMissingTotalUsf: number;
  headcount: number;
  location: number;
  floorsWithNoFloorplansCount: number;
  floorsWithNoSpacesCount: number;
  hasNoFloorplans: boolean;
}

export interface MissingData {
  projectId: number;
  missingFields: MissingProjectDataField[];
  missingBuildingData: MissingBuildingData[];
}

export interface BenchmarkReportCategory {
  id?: number;
  name?: string;
  color?: string;
  gradientColorIndex?: number;
  defaultCategory: string;
  parentId?: number;
}

export enum BenchmarkReportCardType {
  individualWorkspacesAnalysis = 'individual_workspaces_analysis',
  usfAllocationBySpaceCategory = 'usf_allocation_by_space_category',
  workSeatRatios = 'work_seat_ratios',
  usfMetrics = 'usf_metrics',
}

export enum BenchmarkReportIconType {
  individualWorkspaces = 'individual_workspaces',
  meetingSpaces = 'meeting_spaces',
  amenitySpaces = 'amenity_spaces',
  supportSpaces = 'support_spaces',
  workstations = 'workstations',
  privateOffices = 'private_offices',
  otherWorkpoints = 'other_workpoints',
  workSeat = 'work_seat',
  headcount = 'headcount',
}

export enum DismissableBenchmarkIssue {
  iwSpacesMissingUsf = 'iw_spaces_missing_usf',
  iwSpacesMissingSeatcount = 'iw_spaces_missing_seatcount',
  meetingSpacesMissingUsf = 'meeting_spaces_missing_usf',
  meetingSpacesMissingSeatcount = 'meeting_spaces_missing_seatcount',
  amenitySpacesMissingUsf = 'amenity_spaces_missing_usf',
  amenitySpacesMissingSeatcount = 'amenity_spaces_missing_seatcount',
  supportSpacesMissingUsf = 'support_spaces_missing_usf',
  floorsMissingTotalUsf = 'floors_missing_total_usf',
  projectLocation = 'project_location',
  projectHeadcount = 'project_headcount',
}

export type DismissedIssuesMap = {
  [key in DismissableBenchmarkIssue]: boolean;
};

export interface BenchmarkReportIcon {
  id?: number;
  iconPath?: string;
  iconType?: BenchmarkReportIconType;
  cardType?: BenchmarkReportCardType;
}

export interface BenchmarkReport {
  id?: number;
  name?: string;
  verified?: boolean;
  status?: string;
  benchmarkProjects: BenchmarkProject[];
  owner?: IUser;
  isMarkedAsFavoriteByUser?: boolean;
  summary?: BenchmarkReportSummary;
  insertedAt?: string;
  updatedAt?: string;
  lastEditor?: IUser;
  creator?: IUser;
  lastVerifiedAt?: string;
  verifier?: IUser;
  currentUserRole?: UserRoleInReport;
  isMember?: boolean;
  linkedProjectsCount?: number;
  industries?: string[];
  organizationId?: number;
  categories?: BenchmarkReportCategory[];
  icons?: BenchmarkReportIcon[];
}

export interface BenchmarkReportSubcategoryAnalysis {
  averageSquareFeet: number;
  name: string;
  seatMix: number;
  subcategory: string;
}

export interface BenchmarkReportCategoryUSFAllocation {
  category: string;
  usfAllocationPercent: number;
}

export interface BenchmarkReportUSFMetrics {
  usfPerHeadcount: number;
  usfPerWorkSeat: number;
}

export interface BenchmarkReportWorkSeatRatios {
  meetingSeatsRatio: number;
  sharedSeatsRatio: number;
  headcountRatio: number;
}

export interface BenchmarkReportSummary {
  id?: string;
  name?: string;
  averageProjectSize: number;
  individualWorkspacesAnalysis: BenchmarkReportSubcategoryAnalysis[];
  linkedProjects: number;
  usfAllocationBySpaceCategory: BenchmarkReportCategoryUSFAllocation[];
  usfMetrics: BenchmarkReportUSFMetrics;
  workSeatRatios: BenchmarkReportWorkSeatRatios;
  industries: string[];
}

export interface PaginatedBenchmarkReports {
  total: number;
  reports: BenchmarkReport[];
}

export interface BenchmarkReportsFilter {
  name?: string;
  verified?: boolean;
  userId?: number;
}

export type BenchmarkReportSortOptions = {
  isMarkedAsFavoriteByUser?: SortingDirection;
  id?: SortingDirection;
  name?: SortingDirection;
  owner?: SortingDirection;
  status?: SortingDirection;
};

export interface GetBenchmarkReportsParams {
  perPage: number;
  page: number;
  filter?: BenchmarkReportsFilter;
  sort: BenchmarkReportSortOptions;
}
